import * as React from 'react';
import isNil from 'lodash/isNil';

import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { maxLength } from 'utils/validators';

import './profile-about-bio.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./profile-about-bio.i18n.json'));

import type { InfluencerProfileForm, TextEntry } from 'types';
import type {
  FieldDescriptionShowParams
} from 'modules/influencers/components/influencer-create-profile/InfluencerCreateProfile';

interface Props {
  details: InfluencerProfileForm,
  onSubmit: (details: InfluencerProfileForm) => void,
  onCancel: (details: InfluencerProfileForm) => void,
  onFieldDescriptionShow: (params: FieldDescriptionShowParams) => void,
  onFieldDescriptionHide: () => void
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'profile_about_bio';
const MAX_LENGTH_BIO = 600;

const ProfileBio: React.FC<Props> = ({
  details,
  onSubmit,
  onCancel,
  onFieldDescriptionShow,
  onFieldDescriptionHide
}): JSX.Element => {
  const [updatedDetails, setUpdatedDetails] = React.useState(details);

  const handleFieldUpdate = (
    event: React.SyntheticEvent<TextEntry>
  ): void => {
    const target = event.currentTarget;
    const type = target.dataset?.type;
    const value = target.value ?? '';

    if (isNil(type)) {
      return;
    }

    setUpdatedDetails({
      ...updatedDetails,
      [type]: value
    });
  };

  const handleSubmit = (): void => (
    onSubmit(updatedDetails)
  );

  const handleCancel = (): void => (
    onCancel(updatedDetails)
  );

  const renderInput = (): React.ReactNode => {
    const value = updatedDetails.pitch ?? '';

    return (
      <Input
        autoFocus
        multiline
        value={value}
        name="pitch"
        width="responsive"
        className="profile-about-bio__input"
        onChange={handleFieldUpdate}
        charCount={{
          curCount: value.length,
          maxChars: MAX_LENGTH_BIO
        }}
        data={{ type: 'pitch' }}
      />
    );
  };

  const renderActions = (): React.ReactNode => (
    <FormActions align="center">
      <ActionButton
        color="tertiary"
        onClick={handleCancel}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.back`)}
      </ActionButton>
      <ActionButton
        color="primary"
        type="submit"
      >
        {__t(`${COMMON_MSG_KEY}.action_button.continue`)}
      </ActionButton>
    </FormActions>
  );

  React.useEffect(() => {
    onFieldDescriptionShow({
      name: 'pitch',
      positionTop: 84,
      width: 428
    });

    return () => {
      onFieldDescriptionHide();
    };
  }, []);

  return (
    <div className="profile-about-bio">
      <Form
        onSubmit={handleSubmit}
      >
        <div className="profile-about-bio__column">
          <FormField
            label={__t(`${MSG_KEY}.labels.more_about_you`)}
            labelType="highlighted"
            direction="column"
            validators={[
              maxLength(updatedDetails.pitch, MAX_LENGTH_BIO)
            ]}
          >
            <div className="profile-about-bio__description">
              {__t(`${MSG_KEY}.messages.more_about_you`)}
            </div>
            {renderInput()}
          </FormField>
        </div>
        <div className="profile-about-bio__column">
          {renderActions()}
        </div>
      </Form>
    </div>
  );
};

export default ProfileBio;
